import useHttpProvider from "@/common/hooks/use-http";
import { AuthenticationProvider } from "@/common/providers/authentication";
import { trackCompleteRegistration } from "@/common/services/analytics";
import { notifyError, notifySuccess } from "@/components/ui/sonner";
import { HomeRoute } from "@/features/home/route";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { User } from "../models/user";
import * as ValidationService from "../services/validation";

const ActivateAccountFormSchema = z.object({
	verificationCode: z
		.string()
		.min(6, "Verification code is required")
		.max(6, "Verification code is invalid"),
});

function useActivateAccount(onAccountActivated?: (user: User | null) => void) {
	const { http } = useHttpProvider();
	const navigate = useNavigate();

	const activateAccountForm = useForm<
		z.infer<typeof ActivateAccountFormSchema>
	>({
		resolver: zodResolver(ActivateAccountFormSchema),
	});

	const [showActivateAccountDialog, setShowActivateAccountDialog] =
		useState(false);

	async function onActivateAccountSubmit(
		data: z.infer<typeof ActivateAccountFormSchema>
	) {
		try {
			const { verificationCode } = data;
			const metaEventId = `complete_registration_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

			const response = await ValidationService.validateEmailVerificationCode(
				http,
				verificationCode,
				metaEventId
			);

			setShowActivateAccountDialog(false);
			trackCompleteRegistration({
				status: "verified",
			}, {
				eventID: metaEventId,
			});

			notifySuccess("Authentication", "Account activated successfully.");

			if (!onAccountActivated) {
				await AuthenticationProvider.signin(response);
				navigate(HomeRoute);
				return;
			}

			await onAccountActivated(response);
		} catch {
			notifyError(
				"Uh oh! Something went wrong.",
				"Failed to activate account."
			);
		}
	}

	async function onResendVerificationCode() {
		try {
			await ValidationService.requestEmailVerificationCode(http);
			notifySuccess(
				"Verification code sent",
				"Please check your email for the verification code."
			);
		} catch {
			notifyError(
				"Uh oh! Something went wrong.",
				"Failed to send verification code."
			);
		}
	}

	return {
		onResendVerificationCode,

		activateAccountForm,
		onActivateAccountSubmit,
		showActivateAccountDialog,
		setShowActivateAccountDialog,
	};
}

export default useActivateAccount;
